import React from "react";
import { Box, SimpleGrid, Text, useColorMode } from "@chakra-ui/react";
import { Chart } from "react-google-charts";
import InsightsSection from "./InsightsSection";
import PlaceholderTile from "./PlaceholderTile";
import { formatPeriodLabel } from "../../utils/period";

const JournalSection = ({
  journalStreak = {},
  journalFrequency = [],
  journalCoverage = {},
}) => {
  const { colorMode } = useColorMode();
  const cardBg = colorMode === "light" ? "gray.50" : "gray.600";
  const labelColor = colorMode === "light" ? "gray.600" : "gray.300";

  const hasFreq = Array.isArray(journalFrequency) && journalFrequency.length > 0;
  const hasStreak = journalStreak && Object.keys(journalStreak).length > 0;
  const hasCoverage = journalCoverage && Object.keys(journalCoverage).length > 0;

  const freqChart = hasFreq
    ? [
        ["Period", "Entries"],
        ...journalFrequency.map((f) => [formatPeriodLabel(f.period), Number(f.count)]),
      ]
    : null;

  const freqOptions = {
    chartArea: { width: "75%", height: "65%" },
    backgroundColor: colorMode === "light" ? "#FDFDFD" : "#1A202C",
    legend: "none",
    hAxis: {
      textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      slantedText: true,
      slantedTextAngle: 30,
    },
    vAxis: {
      title: "Entries",
      titleTextStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      format: "0",
      minValue: 0,
    },
    colors: ["#3182CE"],
  };

  return (
    <InsightsSection
      title="Journal Stats"
      description="Frequency, streaks, and how well you journal alongside your trades."
    >
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Current Streak</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasStreak && journalStreak.current_streak != null
              ? `${journalStreak.current_streak} day${journalStreak.current_streak === 1 ? "" : "s"}`
              : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Longest Streak</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasStreak && journalStreak.longest_streak != null
              ? `${journalStreak.longest_streak} day${journalStreak.longest_streak === 1 ? "" : "s"}`
              : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Trade-Day Coverage</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasCoverage && journalCoverage.coverage_pct != null
              ? `${journalCoverage.coverage_pct}%`
              : "—"}
          </Text>
          <Text fontSize="xs" color={labelColor}>
            {hasCoverage && journalCoverage.avg_words_per_entry != null
              ? `${journalCoverage.avg_words_per_entry} avg words/entry`
              : ""}
          </Text>
        </Box>
      </SimpleGrid>

      {hasFreq ? (
        <Box bg={colorMode === "light" ? "white" : "gray.700"} rounded="md" h="280px">
          <Chart
            chartType="LineChart"
            width="100%"
            height="100%"
            data={freqChart}
            options={freqOptions}
          />
        </Box>
      ) : (
        <PlaceholderTile message="No journal entries yet — write one to start your streak." />
      )}
    </InsightsSection>
  );
};

export default JournalSection;
